import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { COMPANY_LOGO_SRC } from '../../constants/branding';

const BLUE = '#101f78';
const RED = '#cf3e35';
const A4_WIDTH_IN = '8.27in';
const A4_HEIGHT_IN = '11.69in';

const formatYmd = (d) => {
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return new Date().toISOString().split('T')[0];
  return date.toISOString().split('T')[0];
};

/** Collection / invoice anchor date (invoice date = collection date). */
const collectionReferenceMs = (collection) => {
  const raw = collection?.collectionDate ?? collection?.createdAt;
  if (raw == null) return Date.now();
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? Date.now() : d.getTime();
};

const formatRm = (n) => `RM ${Number(n || 0).toFixed(2)}`;

const invoiceNoFromCollection = (collection) => {
  const digits = (collection?.collectionNo || '').replace(/\D/g, '');
  if (digits) return `INC${digits.padStart(10, '0').slice(-10)}`;
  const idTail = `${collection?._id || collection?.id || ''}`.replace(/[^0-9a-f]/gi, '').slice(-10).padStart(10, '0');
  return `INC${idTail}`;
};

const collectionNumberFromCollection = (collection) => {
  const digits = (collection?.collectionNo || '').replace(/\D/g, '');
  if (!digits) return '00000';
  return digits.slice(-5).padStart(5, '0');
};

/** PDF filename base: INV# + collection number */
const invoicePdfDownloadBase = (collection) => {
  const no = (collection?.collectionNo || '').toString().trim();
  if (no) return `INV#${no}`.replace(/[^\w#.-]+/g, '_');
  return `INV#${collectionNumberFromCollection(collection)}`.replace(/[^\w#.-]+/g, '_');
};

const lineDescription = (box) => {
  if (box?.boxType) return box.boxType;
  if (box?.description) return box.description;
  return 'JET L BOX';
};

const normalizeContact = (person) => {
  const mobile = person?.mobiles;
  if (Array.isArray(mobile)) return mobile.filter(Boolean).join(' / ');
  if (typeof mobile === 'string') return mobile;
  return '';
};

const Invoice = ({ collection, shipper, onClose, onPrint }) => {
  const invoiceTo = shipper || {};
  const rootRef = useRef(null);
  const downloadLock = useRef(false);
  const logoTriedFallback = useRef(false);
  const [logoSrc, setLogoSrc] = useState(COMPANY_LOGO_SRC);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [inputs, setInputs] = useState({
    invoiceNo: '',
    invoiceDate: formatYmd(new Date()),
    dueDate: formatYmd(Date.now() + 7 * 24 * 60 * 60 * 1000),
  });

  useEffect(() => {
    logoTriedFallback.current = false;
    setLogoSrc(COMPANY_LOGO_SRC);
    const baseMs = collectionReferenceMs(collection);
    setInputs((prev) => ({
      ...prev,
      invoiceNo: invoiceNoFromCollection(collection),
      invoiceDate: formatYmd(baseMs),
      dueDate: formatYmd(baseMs + 7 * 24 * 60 * 60 * 1000),
    }));
  }, [collection]);

  const handleDownloadPdf = useCallback(async () => {
    const root = rootRef.current;
    if (!root || downloadLock.current || !collection) return;
    const pages = root.querySelectorAll('.invoice-page');
    if (!pages.length) return;
    downloadLock.current = true;
    setDownloadBusy(true);
    try {
      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      for (let i = 0; i < pages.length; i += 1) {
        const el = pages[i];
        const canvas = await html2canvas(el, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          ignoreElements: (node) => node.classList?.contains('invoice-hide-print'),
        });
        const imgData = canvas.toDataURL('image/jpeg', 0.92);
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfW, pdfH, undefined, 'FAST');
      }
      pdf.save(`${invoicePdfDownloadBase(collection)}.pdf`);
    } catch (e) {
      console.error(e);
      alert('Could not create PDF. Try Print and choose Save as PDF instead.');
    } finally {
      downloadLock.current = false;
      setDownloadBusy(false);
    }
  }, [collection]);

  const rows = useMemo(() => {
    if (collection?.boxes?.length) {
      return collection.boxes.map((box, idx) => {
        const qty = parseInt(box.quantity, 10) || 1;
        const price = parseFloat(box.price) || 0;
        return {
          no: idx + 1,
          itemType: box.boxType || '-',
          description: box.description || lineDescription(box),
          qty,
          price,
          total: qty * price,
        };
      });
    }
    return [{ no: 1, itemType: 'JET L BOX', description: 'JET L BOX', qty: 1, price: 0, total: 0 }];
  }, [collection?.boxes]);

  const totals = useMemo(() => {
    const subTotal = rows.reduce((sum, row) => sum + row.total, 0);
    const ch = collection?.charges || {};
    const packaging = Number.parseFloat(ch.packingCharge) || 0;
    const transport = Number.parseFloat(ch.transportCharge) || 0;
    const otherCharge = Number.parseFloat(ch.otherCharge) || 0;
    const discountType = ch.discountType === 'amount' ? 'amount' : 'percentage';
    const discountValue = Number.parseFloat(ch.discountValue) || 0;
    let discountAmount = 0;
    if (discountType === 'percentage') {
      discountAmount = subTotal * (discountValue / 100);
    } else {
      discountAmount = discountValue;
    }
    const discountLabel = discountType === 'percentage' ? `${discountValue}%` : formatRm(discountAmount);
    const grandTotal = subTotal + packaging + transport + otherCharge - discountAmount;
    return {
      subTotal,
      packaging,
      transport,
      otherCharge,
      discountLabel,
      discountAmount,
      grandTotal,
    };
  }, [rows, collection?.charges]);

  const FIRST_PAGE_ROWS = 20;
  const NEXT_PAGE_ROWS = 30;

  const pagedRows = useMemo(() => {
    if (rows.length <= FIRST_PAGE_ROWS) return [rows];
    const pages = [rows.slice(0, FIRST_PAGE_ROWS)];
    let cursor = FIRST_PAGE_ROWS;
    while (cursor < rows.length) {
      pages.push(rows.slice(cursor, cursor + NEXT_PAGE_ROWS));
      cursor += NEXT_PAGE_ROWS;
    }
    return pages;
  }, [rows]);

  const renderTable = (pageRows, isLastPage, isFirstPage) => {
    const rowCapacity = isFirstPage ? FIRST_PAGE_ROWS : NEXT_PAGE_ROWS;
    const emptyRows = isLastPage ? Math.max(rowCapacity - pageRows.length, 0) : 0;

    return (
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '14px', tableLayout: 'fixed' }}>
        <thead>
          <tr style={{ color: '#fff', fontWeight: 700 }}>
            <th style={{ width: '52px', backgroundColor: BLUE, border: '1px solid #101f78', padding: '6px' }}>NO</th>
            <th style={{ width: '172px', backgroundColor: BLUE, border: '1px solid #101f78', padding: '6px', textAlign: 'left' }}>ITEM TYPE</th>
            <th style={{ backgroundColor: BLUE, border: '1px solid #101f78', padding: '6px', textAlign: 'left' }}>DESCRIPTION</th>
            <th style={{ width: '70px', backgroundColor: BLUE, border: '1px solid #101f78', padding: '6px' }}>QTY</th>
            <th style={{ width: '108px', backgroundColor: RED, border: `1px solid ${RED}`, padding: '6px', textAlign: 'right' }}>PRICE</th>
            <th style={{ width: '108px', backgroundColor: RED, border: `1px solid ${RED}`, padding: '6px', textAlign: 'right' }}>TOTAL</th>
          </tr>
        </thead>
        <tbody>
          {pageRows.map((row) => (
            <tr key={`line-${row.no}`}>
              <td style={{ borderBottom: '1px solid #d1d5db', textAlign: 'center', padding: '0 6px', height: '22px' }}>{row.no}</td>
              <td style={{ borderBottom: '1px solid #d1d5db', padding: '0 6px', height: '22px', wordBreak: 'break-word' }}>{row.itemType}</td>
              <td style={{ borderBottom: '1px solid #d1d5db', padding: '0 6px', height: '22px' }}>{row.description}</td>
              <td style={{ borderBottom: '1px solid #d1d5db', textAlign: 'center', padding: '0 6px', height: '22px' }}>{row.qty}</td>
              <td style={{ borderBottom: '1px solid #d1d5db', textAlign: 'right', padding: '0 6px', height: '22px' }}>{row.price.toFixed(2)}</td>
              <td style={{ borderBottom: '1px solid #d1d5db', textAlign: 'right', padding: '0 6px', height: '22px' }}>{row.total.toFixed(2)}</td>
            </tr>
          ))}
          {Array.from({ length: emptyRows }).map((_, i) => (
            <tr key={`empty-${i}`}>
              <td style={{ borderBottom: '1px solid #d1d5db', padding: '0 6px', height: '22px' }} />
              <td style={{ borderBottom: '1px solid #d1d5db', height: '22px' }} />
              <td style={{ borderBottom: '1px solid #d1d5db', height: '22px' }} />
              <td style={{ borderBottom: '1px solid #d1d5db', height: '22px' }} />
              <td style={{ borderBottom: '1px solid #d1d5db', height: '22px' }} />
              <td style={{ borderBottom: '1px solid #d1d5db', height: '22px' }} />
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div ref={rootRef} className="invoice-print-root" style={{ backgroundColor: '#f3f4f6' }}>
      <style>
        {`
          .invoice-page {
            width: ${A4_WIDTH_IN};
            min-height: ${A4_HEIGHT_IN};
            margin: 0 auto 12px;
            padding: 8mm;
            box-sizing: border-box;
            color: #1f2937;
            font-family: "Poppins", Arial, sans-serif;
            font-size: 12px;
            background: #fff;
            display: flex;
            flex-direction: column;
          }
          @media print {
            html, body {
              width: 210mm;
              margin: 0;
              padding: 0;
              background: #fff;
            }
            body * {
              visibility: hidden;
            }
            .invoice-print-root,
            .invoice-print-root * {
              visibility: visible;
            }
            .invoice-print-root {
              position: absolute !important;
              left: 0 !important;
              top: 0 !important;
              width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
              background: #fff !important;
            }
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            @page { size: A4; margin: 0; }
            .invoice-print-root {
              background: #fff !important;
            }
            .invoice-page {
              width: ${A4_WIDTH_IN} !important;
              min-height: ${A4_HEIGHT_IN} !important;
              margin: 0 !important;
              page-break-after: always;
              break-after: page;
            }
            .invoice-page:last-child {
              page-break-after: auto;
              break-after: auto;
            }
            .invoice-hide-print { display: none !important; }
          }
        `}
      </style>

      {pagedRows.map((pageRows, pageIndex) => {
        const isFirstPage = pageIndex === 0;
        const isLastPage = pageIndex === pagedRows.length - 1;
        return (
          <div className="invoice-page" key={`invoice-page-${pageIndex}`}>
            {isFirstPage && (
              <>
                <div className="invoice-hide-print" style={{ textAlign: 'right', marginBottom: '12px' }}>
                  <button
                    type="button"
                    onClick={onClose}
                    style={{ border: 'none', background: '#374151', color: '#fff', padding: '8px 14px', cursor: 'pointer' }}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={onPrint}
                    style={{ border: 'none', background: '#111827', color: '#fff', padding: '8px 14px', marginLeft: '8px', cursor: 'pointer' }}
                  >
                    Print (A4)
                  </button>
                  <button
                    type="button"
                    onClick={handleDownloadPdf}
                    disabled={downloadBusy}
                    style={{
                      border: 'none',
                      background: downloadBusy ? '#9ca3af' : 'var(--jet-royal, #0056f1)',
                      color: '#fff',
                      padding: '8px 14px',
                      marginLeft: '8px',
                      cursor: downloadBusy ? 'wait' : 'pointer',
                    }}
                  >
                    {downloadBusy ? 'Preparing…' : 'Download PDF (A4)'}
                  </button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', paddingBottom: '2px' }}>
                  <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                    <img
                      src={logoSrc}
                      alt="Jet Shipping Logo"
                      style={{ width: '96px', height: '96px', objectFit: 'contain', display: 'block' }}
                      onError={() => {
                        if (!logoTriedFallback.current) {
                          logoTriedFallback.current = true;
                          setLogoSrc('/images/logo/jet-shipping-logo.png');
                        }
                      }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0, paddingTop: '2px' }}>
                    <div
                      style={{
                        color: RED,
                        fontSize: '30px',
                        fontWeight: 800,
                        lineHeight: 1.15,
                        letterSpacing: '0.07em',
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      JET SHIPPING CARGO - MALAYSIA
                    </div>
                    <div style={{ marginTop: '12px', paddingTop: '14px', borderTop: '1px solid #e5e7eb' }}>
                      <p style={{ margin: 0, fontSize: '13px', color: '#4b5563', lineHeight: 1.6 }}>
                        No.AZ-B1-07, Lot No.1B/2(A), Anzen Business Park, Jalan 4/37A, Kawasan Industri Taman Bukit
                        <br />
                        Maluri, Kepong, Kuala Lumpur, 52100
                      </p>
                      <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#374151' }}>
                        <span style={{ color: '#9ca3af', fontWeight: 700, fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Email</span>
                        <span style={{ margin: '0 6px', color: '#d1d5db' }}>|</span>
                        <span style={{ color: BLUE, fontWeight: 600 }}>jetshippingandlogistic@gmail.com</span>
                      </p>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', height: '4px', marginTop: '14px', borderRadius: '1px', overflow: 'hidden' }}>
                  <div style={{ width: '100px', backgroundColor: RED, flexShrink: 0 }} />
                  <div style={{ flex: 1, backgroundColor: BLUE }} />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '40px', marginTop: '14px' }}>
                  <div>
                    <div style={{ color: RED, fontWeight: 800, fontSize: '26px', marginBottom: '8px' }}>INVOICE</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', rowGap: '4px' }}>
                      <div>Invoice No</div><div>{inputs.invoiceNo}</div>
                      <div>Invoice Date</div><div>{inputs.invoiceDate}</div>
                      <div>Due Date</div><div>{inputs.dueDate}</div>
                      <div>Collection Number</div><div>{collectionNumberFromCollection(collection)}</div>
                    </div>
                  </div>
                  <div>
                    <div style={{ color: RED, fontWeight: 800, fontSize: '26px', marginBottom: '8px' }}>INVOICE TO</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', rowGap: '4px' }}>
                      <div>Name</div><div>{invoiceTo.nameWithInitials || 'N/A'}</div>
                      <div>Passport / ID</div><div>{invoiceTo.passportNo || invoiceTo.nicNo || collection?.shipperPassportNo || 'N/A'}</div>
                      <div>Contact</div><div>{normalizeContact(invoiceTo) || 'N/A'}</div>
                      <div>Address</div><div>{invoiceTo.address || 'N/A'}</div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {!isFirstPage && (
              <>
                <div style={{ color: RED, fontWeight: 700, fontSize: '18px', marginBottom: '6px' }}>INVOICE CONTINUED</div>
                <div style={{ borderTop: `4px solid ${BLUE}`, marginTop: '2px' }} />
              </>
            )}

            {renderTable(pageRows, isLastPage, isFirstPage)}
            <div style={{ borderTop: `4px solid ${BLUE}`, marginTop: '10px' }} />

            {isLastPage && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: '10px', gap: '18px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: BLUE, fontWeight: 700, fontSize: '14px', marginBottom: '4px' }}>Payment Informations :</div>
                    <div style={{ fontSize: '12px', lineHeight: 1.45, color: '#111827' }}>
                      Account Name: Jet shipping and logistics
                      <br />
                      Account No: 141490010063565
                      <br />
                      Alliance bank
                    </div>
                  </div>

                  <div style={{ width: '320px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <tbody>
                        <tr><td style={{ padding: '3px 0' }}>Sub Total</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{formatRm(totals.subTotal)}</td></tr>
                        <tr><td style={{ padding: '3px 0' }}>Packaging Charge</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{formatRm(totals.packaging)}</td></tr>
                        <tr><td style={{ padding: '3px 0' }}>Transport</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{formatRm(totals.transport)}</td></tr>
                        <tr><td style={{ padding: '3px 0' }}>Other Charges</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{formatRm(totals.otherCharge)}</td></tr>
                        <tr><td style={{ padding: '3px 0' }}>Discount</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{totals.discountLabel}</td></tr>
                        <tr>
                          <td style={{ color: '#111827', fontWeight: 700, paddingTop: '8px' }}>TOTAL</td>
                          <td style={{ color: RED, textAlign: 'right', fontWeight: 800, fontSize: '19px', paddingTop: '8px' }}>{formatRm(Math.max(0, totals.grandTotal))}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div style={{ marginTop: 'auto' }} />
                <div style={{ borderTop: `4px solid ${BLUE}`, marginTop: '14px', paddingTop: '6px', fontSize: '12px' }}>Terms & Conditions Apply</div>
                <div style={{ marginTop: '8px', backgroundColor: RED, color: '#fff', textAlign: 'center', padding: '8px 10px', fontWeight: 600, fontSize: '11px' }}>
                  WHATSAPP:+60 11-2119 0765 | OFFICE:+60 3-6262 0505 | HOTLINE:+60 11-6053 6205
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default Invoice;
